<template>
  <div
    ref="viewerRootRef"
    class="docx-viewer-component flex flex-col w-full rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900 shadow-sm transition-all"
    :class="{ 'fixed inset-0 z-50 rounded-none h-screen max-h-screen bg-gray-900/95': isFullscreen }"
  >
    <!-- Top Toolbar -->
    <div
      v-if="showToolbar"
      class="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xs px-3 sm:px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2 flex-wrap text-xs select-none z-10"
    >
      <!-- Left: File details & Page count -->
      <div class="flex items-center gap-2 min-w-0">
        <div class="p-1.5 rounded-lg bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex-shrink-0">
          <FileText class="w-4 h-4" />
        </div>
        <div class="truncate">
          <span class="font-medium text-gray-800 dark:text-gray-200 truncate block text-xs">
            {{ fileName || 'Word Document' }}
          </span>
          <span v-if="pageCount > 0" class="text-[10px] text-gray-500 dark:text-gray-400 block -mt-0.5">
            {{ pageCount }} page{{ pageCount === 1 ? '' : 's' }}
          </span>
        </div>
      </div>

      <!-- Right: Zoom & Action Controls -->
      <div class="flex items-center gap-1 sm:gap-1.5 ml-auto">
        <!-- Zoom controls -->
        <div class="flex items-center bg-gray-100 dark:bg-gray-700/60 rounded-xl p-0.5 border border-gray-200/80 dark:border-gray-600/60">
          <button
            type="button"
            class="p-1 rounded-lg hover:bg-white dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition"
            title="Zoom out"
            :disabled="zoom <= 50"
            @click="zoomOut"
          >
            <ZoomOut class="w-3.5 h-3.5" />
          </button>

          <button
            type="button"
            class="px-2 py-0.5 text-[11px] font-semibold text-gray-700 dark:text-gray-200 hover:text-secondary min-w-[42px] text-center"
            title="Reset zoom to 100%"
            @click="resetZoom"
          >
            {{ zoom }}%
          </button>

          <button
            type="button"
            class="p-1 rounded-lg hover:bg-white dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition"
            title="Zoom in"
            :disabled="zoom >= 200"
            @click="zoomIn"
          >
            <ZoomIn class="w-3.5 h-3.5" />
          </button>
        </div>

        <div class="h-4 w-px bg-gray-200 dark:bg-gray-700 mx-0.5 hidden sm:block"></div>

        <!-- Fullscreen toggle -->
        <button
          type="button"
          class="p-1.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition"
          :title="isFullscreen ? 'Exit Fullscreen' : 'View Fullscreen'"
          @click="toggleFullscreen"
        >
          <Minimize2 v-if="isFullscreen" class="w-3.5 h-3.5" />
          <Maximize2 v-else class="w-3.5 h-3.5" />
        </button>

        <!-- Download button -->
        <button
          v-if="allowDownload"
          type="button"
          class="px-2.5 py-1 rounded-xl bg-secondary hover:bg-secondary-hover text-white font-medium text-xs flex items-center gap-1 shadow-xs transition"
          title="Download original document"
          @click="handleDownload"
        >
          <Download class="w-3.5 h-3.5" />
          <span class="hidden sm:inline">Download</span>
        </button>
      </div>
    </div>

    <!-- Viewer Body / Scrollable Canvas -->
    <div
      class="docx-canvas-container flex-1 overflow-auto relative p-4 sm:p-6"
      :style="{ maxHeight: isFullscreen ? 'calc(100vh - 50px)' : maxHeight }"
    >
      <!-- Loading State -->
      <div v-if="isLoading" class="flex flex-col items-center justify-center py-20 space-y-3">
        <div class="w-9 h-9 border-3 border-secondary border-t-transparent rounded-full animate-spin"></div>
        <p class="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300">
          Rendering Word document preview...
        </p>
        <span class="text-[11px] text-gray-400">Processing document structure and styles</span>
      </div>

      <!-- Render Error State -->
      <div
        v-else-if="errorMessage"
        class="flex flex-col items-center justify-center py-16 px-4 text-center max-w-md mx-auto space-y-3"
      >
        <div class="w-12 h-12 rounded-2xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center">
          <AlertTriangle class="w-6 h-6" />
        </div>
        <div class="space-y-1">
          <h4 class="text-sm font-bold text-gray-800 dark:text-gray-100">
            Inline Preview Unavailable
          </h4>
          <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            {{ errorMessage }}
          </p>
        </div>
        <div class="flex items-center gap-2 pt-2">
          <button
            type="button"
            class="px-3.5 py-1.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 text-xs font-semibold flex items-center gap-1.5 transition"
            @click="renderDocument"
          >
            <RefreshCw class="w-3.5 h-3.5" />
            <span>Try Again</span>
          </button>
          <button
            type="button"
            class="px-3.5 py-1.5 rounded-xl bg-secondary hover:bg-secondary-hover text-white text-xs font-semibold flex items-center gap-1.5 transition shadow-xs"
            @click="handleDownload"
          >
            <Download class="w-3.5 h-3.5" />
            <span>Download Document</span>
          </button>
        </div>
      </div>

      <!-- Rendered Document Target Container -->
      <div
        v-show="!isLoading && !errorMessage"
        class="docx-zoom-wrapper w-full flex justify-center transition-transform duration-150 origin-top"
        :style="zoomStyle"
      >
        <div
          ref="docxContainerRef"
          class="docx-render-target w-full flex flex-col items-center"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { renderAsync } from 'docx-preview'
import {
  FileText,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  Download,
  AlertTriangle,
  RefreshCw,
} from 'lucide-vue-next'

const props = withDefaults(
  defineProps<{
    src: Blob | ArrayBuffer | string | null | undefined
    fileName?: string
    maxHeight?: string
    showToolbar?: boolean
    allowDownload?: boolean
    initialZoom?: number
  }>(),
  {
    fileName: '',
    maxHeight: '600px',
    showToolbar: true,
    allowDownload: false,
    initialZoom: 100,
  }
)

const emit = defineEmits<{
  (e: 'download'): void
  (e: 'rendered', pageCount: number): void
  (e: 'error', error: any): void
}>()

const viewerRootRef = ref<HTMLElement | null>(null)
const docxContainerRef = ref<HTMLElement | null>(null)
const isLoading = ref(false)
const errorMessage = ref<string | null>(null)
const zoom = ref(props.initialZoom)
const pageCount = ref(0)
const isFullscreen = ref(false)

const zoomStyle = computed(() => {
  if (typeof CSS !== 'undefined' && CSS.supports && CSS.supports('zoom', '1')) {
    return {
      zoom: `${zoom.value}%`,
    }
  }
  const scale = zoom.value / 100
  return {
    transform: `scale(${scale})`,
    transformOrigin: 'top center',
    width: scale > 1 ? `${scale * 100}%` : '100%',
  }
})

const zoomIn = () => {
  if (zoom.value < 200) {
    zoom.value = Math.min(200, zoom.value + 15)
  }
}

const zoomOut = () => {
  if (zoom.value > 50) {
    zoom.value = Math.max(50, zoom.value - 15)
  }
}

const resetZoom = () => {
  zoom.value = 100
}

const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value
}

const handleDownload = () => {
  emit('download')
}

const handleKeydown = (e: KeyboardEvent) => {
  if (isFullscreen.value && e.key === 'Escape') {
    isFullscreen.value = false
  }
}

const extractData = async (): Promise<Blob | ArrayBuffer | null> => {
  if (!props.src) return null

  if (props.src instanceof Blob || props.src instanceof ArrayBuffer) {
    return props.src
  }

  if (typeof props.src === 'string') {
    const response = await fetch(props.src)
    if (!response.ok) {
      throw new Error(`Failed to fetch document: ${response.statusText}`)
    }
    return await response.blob()
  }

  return null
}

const renderDocument = async () => {
  if (!props.src) {
    errorMessage.value = null
    pageCount.value = 0
    return
  }

  isLoading.value = true
  errorMessage.value = null
  pageCount.value = 0

  try {
    const data = await extractData()
    if (!data) {
      throw new Error('No document data available')
    }

    if (!docxContainerRef.value) {
      return
    }

    // Clear previous render
    docxContainerRef.value.innerHTML = ''

    await renderAsync(data, docxContainerRef.value, undefined, {
      className: 'docx',
      inWrapper: true,
      ignoreWidth: false,
      ignoreHeight: false,
      breakPages: true,
      ignoreLastRenderedPageBreak: false,
      experimental: true,
      trimXmlDeclaration: true,
      renderHeaders: true,
      renderFooters: true,
      renderFootnotes: true,
      renderEndnotes: true,
      useBase64URL: true,
    })

    // Detect page count
    const pages = docxContainerRef.value.querySelectorAll('section.docx')
    pageCount.value = pages.length || 1

    emit('rendered', pageCount.value)
  } catch (err: any) {
    console.error('Docx rendering error:', err)
    const errText = err?.message || String(err)
    if (
      errText.toLowerCase().includes('zip') ||
      errText.toLowerCase().includes('end of central directory') ||
      props.fileName.toLowerCase().endsWith('.doc')
    ) {
      errorMessage.value =
        'This document appears to be in an older Word format (.doc) or a protected archive. Please download the file to view it in Microsoft Word.'
    } else {
      errorMessage.value =
        'An error occurred while parsing the Word document. Please download the file to view its full contents.'
    }
    emit('error', err)
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.src,
  () => {
    renderDocument()
  }
)

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
  renderDocument()
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
/* Deep styles for docx-preview wrapper & pages */
.docx-viewer-component :deep(.docx-wrapper) {
  background: transparent !important;
  padding: 0 !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  width: 100% !important;
}

.docx-viewer-component :deep(.docx-wrapper > section.docx) {
  background: #ffffff !important;
  color: #111827 !important;
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.15), 0 2px 6px -1px rgba(0, 0, 0, 0.08) !important;
  border-radius: 4px !important;
  margin-bottom: 24px !important;
  box-sizing: border-box !important;
}

/* Force document page text to be dark readable text against white paper */
.docx-viewer-component :deep(.docx-wrapper > section.docx *) {
  color-scheme: light !important;
}

/* Make images inside docx responsive so they never overflow the page */
.docx-viewer-component :deep(.docx-wrapper img) {
  max-width: 100% !important;
  height: auto !important;
}

/* Tables inside docx should not break page bounds */
.docx-viewer-component :deep(.docx-wrapper table) {
  max-width: 100% !important;
}
</style>

